import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { PLAN_CREATION_REASONS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import type { PlanCreationStep } from '../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { PLAN_CREATION_STEPS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { CreatePlanProvider } from '../../../contexts/create-plan-context/CreatePlanProvider/CreatePlanProvider';
import { FormErrorNotificationProvider } from '../../../contexts/form-error-notification-context/FormErrorNotificationProvider';
import { prefetchMeters } from '../../../hooks/requests/use-meters/use-meters';
import { CreatePlanErrorNotification } from './create-plan-page-components/CreatePlanErrorNotification/CreatePlanErrorNotification';
import { CreatePlanPageHeader } from './create-plan-page-components/CreatePlanPageHeader';
import { CreatePlanPageStepperNavigator } from './create-plan-page-components/CreatePlanPageStepperNavigator';
import { CreatePlanPageTabs } from './create-plan-page-components/CreatePlanPageTabs/CreatePlanPageTabs';
import { CreatePlanPageContent } from './create-plan-page-components/NewCreatePlanPageContent/CreatePlanPageContent';
import { PlanSettings } from './create-plan-page-components/PlanSettings';
import { PLAN_CREATION_STEPS_CTA_LABELS } from './create-plan-page-consts/create-plan-default-values';
import { withPrefetch } from '../../../components/hocs';
import { CreatePlanForm } from './create-plan-page-components/CreatePlanForm/CreatePlanForm';
import { isNotEmptyString, isNotNullOrUndefined } from '@vy/utils';
import { useGetContractPrerequisites } from './create-plan-page-hooks/useGetContractPrerequisites';
import { usePlanSubmitter } from './create-plan-page-hooks/usePlanSubmitter';
import { useContractSubmitter } from './create-plan-page-hooks/useContractSubmitter';
import { useSearchParams } from 'react-router-dom';
import { CREATE_PLAN_QUERY_PARAMS } from './create-plan-page-consts/query-params';

const PLAN_CREATION_STEPS_CONTENT: Record<PlanCreationStep, React.ReactElement> = {
  [PLAN_CREATION_STEPS.pricingAndProducts]: <CreatePlanPageTabs />,
  [PLAN_CREATION_STEPS.settings]: <PlanSettings />,
};

const CreatePlanPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const cloneFromPlanIdQueryParam = searchParams.get(CREATE_PLAN_QUERY_PARAMS.CLONE_FROM_PLAN_ID);

  const contractPrerequisites = useGetContractPrerequisites();

  const isCloningPlan = isNotEmptyString(cloneFromPlanIdQueryParam);
  const planCreationReason = isNotNullOrUndefined(contractPrerequisites)
    ? PLAN_CREATION_REASONS.NewContract
    : PLAN_CREATION_REASONS.NewPlan;

  const planSubmitter = usePlanSubmitter();
  const contractSubmitter = useContractSubmitter();

  const onFormSubmissionHandler = useMemo(() => {
    return planCreationReason === PLAN_CREATION_REASONS.NewPlan ? planSubmitter : contractSubmitter;
  }, [contractSubmitter, planSubmitter, planCreationReason]);

  return (
    <>
      <Helmet>
        <title>Plans</title>
      </Helmet>

      <CreatePlanProvider
        planCreationReason={planCreationReason}
        defaultPlanCreationStep={PLAN_CREATION_STEPS.settings}
        onFormSubmission={onFormSubmissionHandler}
        isCloningPlan={isCloningPlan}
      >
        <CreatePlanForm>
          <FormErrorNotificationProvider>
            <CreatePlanPageHeader />

            <CreatePlanPageContent stepsContent={PLAN_CREATION_STEPS_CONTENT} />

            <CreatePlanErrorNotification />

            <CreatePlanPageStepperNavigator stepsLabels={PLAN_CREATION_STEPS_CTA_LABELS} formClearable />
          </FormErrorNotificationProvider>
        </CreatePlanForm>
      </CreatePlanProvider>
    </>
  );
};

const CreatePlanPageWithSuspenseBoundary = withSuspenseBoundary(
  withGlobalSearchParams(withPrefetch(CreatePlanPage, prefetchMeters)),
);

export { CreatePlanPageWithSuspenseBoundary as CreatePlanPage };
